import type { CopilotMode } from '@terra/shared-dto-chat-types';

export type SlashCommandKind = 'mode' | 'action' | 'skill';

export type SlashCommandItem = {
  id: string;
  name: string;
  label: string;
  description: string;
  kind: SlashCommandKind;
  argumentHint?: string;
  aliases?: string[];
  modeTarget?: CopilotMode;
  skillSlug?: string;
  onExecute?: (args: { rawArgs: string }) => void | Promise<void>;
};

export type ParsedSlashInput = {
  commandName: string;
  rawArgs: string;
  hasSpaceAfterCommand: boolean;
};

export function parseSlashCommandInput(input: string): ParsedSlashInput | null {
  if (input.includes('\n') || input.includes('\r')) {
    return null;
  }

  const trimmedStart = input.trimStart();

  if (!trimmedStart.startsWith('/')) {
    return null;
  }

  const withoutSlash = trimmedStart.slice(1);
  const firstWhitespaceIndex = withoutSlash.search(/\s/);

  if (firstWhitespaceIndex === -1) {
    return {
      commandName: withoutSlash.toLowerCase(),
      rawArgs: '',
      hasSpaceAfterCommand: false,
    };
  }

  const commandName = withoutSlash.slice(0, firstWhitespaceIndex).toLowerCase();
  const rawArgs = withoutSlash.slice(firstWhitespaceIndex + 1).trim();

  return {
    commandName,
    rawArgs,
    hasSpaceAfterCommand: true,
  };
}

export function extractSlashCommandQuery(input: string): string | null {
  if (input.includes('\n')) {
    return null;
  }

  const parsed = parseSlashCommandInput(input);

  if (!parsed || parsed.hasSpaceAfterCommand) {
    return null;
  }

  return parsed.commandName;
}

export function scoreSlashCommandMatch({
  command,
  query,
}: {
  command: SlashCommandItem;
  query: string;
}): number {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return command.kind === 'skill' ? 10 : 100;
  }

  const nameLower = command.name.toLowerCase();
  const aliasesLower = (command.aliases ?? []).map((alias) =>
    alias.toLowerCase(),
  );

  if (nameLower === normalizedQuery) {
    return 1000;
  }

  if (aliasesLower.includes(normalizedQuery)) {
    return 950;
  }

  if (nameLower.startsWith(normalizedQuery)) {
    return 800 - (nameLower.length - normalizedQuery.length);
  }

  if (aliasesLower.some((alias) => alias.startsWith(normalizedQuery))) {
    return 700;
  }

  if (nameLower.includes(normalizedQuery)) {
    return 500;
  }

  if (command.description.toLowerCase().includes(normalizedQuery)) {
    return 250;
  }

  return 0;
}

export function filterSlashCommands({
  commands,
  query,
}: {
  commands: readonly SlashCommandItem[];
  query: string;
}): SlashCommandItem[] {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return [...commands];
  }

  return commands
    .map((command) => ({
      command,
      score: scoreSlashCommandMatch({ command, query: normalizedQuery }),
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return a.command.name.localeCompare(b.command.name);
    })
    .map(({ command }) => command);
}

export function findExactSlashCommand({
  commands,
  commandName,
}: {
  commands: readonly SlashCommandItem[];
  commandName: string;
}): SlashCommandItem | undefined {
  const normalized = commandName.trim().toLowerCase();

  if (!normalized) {
    return undefined;
  }

  return commands.find(
    (cmd) =>
      cmd.name.toLowerCase() === normalized ||
      (cmd.aliases ?? []).some((alias) => alias.toLowerCase() === normalized),
  );
}

export function resolveDirectSlashCommandInvocation({
  commands,
  input,
}: {
  commands: readonly SlashCommandItem[];
  input: string;
}): { command: SlashCommandItem; rawArgs: string } | null {
  const parsed = parseSlashCommandInput(input);

  if (!parsed || !parsed.commandName) {
    return null;
  }

  const command = findExactSlashCommand({
    commands,
    commandName: parsed.commandName,
  });

  if (!command) {
    return null;
  }

  return {
    command,
    rawArgs: parsed.rawArgs,
  };
}
